import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  CircularProgress,
  Chip,
  Stack,
  Alert,
} from '@mui/material';
import SearchFilters from '../../components/Search/SearchFilters';
import solutionService from '../../services/solution.service';
import { Solution } from '../../types';

const SearchSolutions: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [solutions, setSolutions] = useState<Solution[]>([]);
  const [filteredSolutions, setFilteredSolutions] = useState<Solution[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load all solutions
  useEffect(() => {
    const loadSolutions = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await solutionService.getAvailableSolutions();
        setSolutions(data);

        // Extract unique tags
        const tags = new Set<string>();
        data.forEach((solution: Solution) => {
          solution.tags?.forEach((tag: string) => tags.add(tag));
        });
        setAvailableTags(Array.from(tags).sort());
      } catch (err) {
        console.error('Error loading solutions:', err);
        setError('Erreur lors du chargement des solutions');
      } finally {
        setLoading(false);
      }
    };

    loadSolutions();
  }, []);

  // Filter solutions
  useEffect(() => {
    let filtered = [...solutions];

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (solution) =>
          solution.pattern_title?.toLowerCase().includes(query) ||
          solution.latex_template?.toLowerCase().includes(query)
      );
    }

    // Filter by tags
    if (selectedTags.length > 0) {
      filtered = filtered.filter((solution) =>
        selectedTags.every((tag) => solution.tags?.includes(tag))
      );
    }

    setFilteredSolutions(filtered);
  }, [searchQuery, selectedTags, solutions]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Left Panel - Search Filters */}
        <Grid item xs={12} md={3}>
          <SearchFilters
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            selectedTags={selectedTags}
            onTagsChange={setSelectedTags}
            availableTags={availableTags}
            contentType="solutions"
          />
        </Grid>

        {/* Right Panel - Results */}
        <Grid item xs={12} md={9}>
          <Box>
            {/* Results count */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {filteredSolutions.length} solution{filteredSolutions.length > 1 ? 's' : ''} trouvée{filteredSolutions.length > 1 ? 's' : ''}
            </Typography>

            {/* Results */}
            <Grid container spacing={2}>
              {filteredSolutions.map((solution) => (
                <Grid item xs={12} sm={6} md={4} key={solution.id}>
                  <Card>
                    {solution.pattern_thumbnail_url && (
                      <CardMedia
                        component="img"
                        height="200"
                        image={solution.pattern_thumbnail_url}
                        alt={solution.pattern_title || `Pattern #${solution.pattern}`}
                        sx={{ objectFit: 'contain', backgroundColor: '#f5f5f5' }}
                      />
                    )}
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Solution pour {solution.pattern_title || `Pattern #${solution.pattern}`}
                      </Typography>

                      {solution.skills && solution.skills.length > 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {solution.skills.length} savoir-faire mobilisé{solution.skills.length > 1 ? 's' : ''}
                        </Typography>
                      )}

                      {solution.tags && solution.tags.length > 0 && (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                          {solution.tags.map((tag) => (
                            <Chip key={tag} label={tag} size="small" variant="outlined" />
                          ))}
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {filteredSolutions.length === 0 && (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="text.secondary">
                  Aucune solution ne correspond à vos critères de recherche
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SearchSolutions;
